import { useNotification } from 'admin';
import { isApiFormError, isFieldErrorResponse, useApiAccountsV1SigninRequestCreate } from 'api';
import { Form, type InferType, PhoneNumberField, phoneNumberValidation, useForm, yup, yupResolver } from 'form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Button, Link, Typography } from 'ui';

import { getAuthRoute } from '../../utils/getAuthRoute';

const schema = yup.object().shape({
  phone_number: phoneNumberValidation().required('Phone number is required'),
});

type FormValues = InferType<typeof schema>;

export const SignInPhoneForm = () => {
  const navigate = useNavigate();
  const { open } = useNotification();
  const { mutate, isPending } = useApiAccountsV1SigninRequestCreate({
    mutation: {
      onError(error) {
        if (isApiFormError(error)) {
          open?.({
            description: error?.response?.data.detail,
            message: 'Error signing in',
            type: 'error',
          });
          return;
        }
        if (isFieldErrorResponse(error)) {
          Object.keys(error.response?.data).forEach((field) => {
            form.setError(field as keyof FormValues, { message: error.response.data[field] });
          });
          if (error.response?.data.non_field_errors) {
            open?.({
              description: error?.response?.data.non_field_errors[0],
              message: 'Error signing in',
              type: 'error',
            });
          }
          return;
        }

        open?.({
          message: 'Error signing in',
          type: 'error',
        });
      },
      onSuccess() {
        navigate(getAuthRoute('/sign-in/phone/verify'));
      },
    },
  });

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: FormValues) => {
    mutate({
      data,
    });
  };

  return (
    <Form {...form} onSubmit={onSubmit} data-testid="sign-in-phone-form">
      <PhoneNumberField
        withFlags
        name="phone_number"
        label="Phone number"
        size="medium"
        data-testid="phone-number-field"
        required
        autoFocus
        fullWidth
      />

      <Typography variant="body2" align="right" gutterBottom>
        <Link component={RouterLink} to={getAuthRoute('/sign-in/email')}>
          Sign in with email
        </Link>
      </Typography>

      <Button type="submit" size="large" loading={isPending}>
        Next
      </Button>
    </Form>
  );
};
